import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useFreshData — hook para datos que deben mantenerse frescos.
 *
 * - Ejecuta el fetcher al montar.
 * - Re-ejecuta cuando la pestaña vuelve a estar visible (visibilitychange).
 * - Re-ejecuta al recuperar foco (focus).
 * - Opcionalmente re-ejecuta cada `refreshMs` mientras la pestaña está activa.
 *
 * @param {Function} fetcher   async () => datos
 * @param {Object}   options
 * @param {number}   options.refreshMs      polling (ms). 0 = off. Default: 5 min.
 * @param {number}   options.staleAfterMs   solo re-fetch al volver a la pestaña si
 *                                          han pasado al menos estos ms desde el último fetch.
 *                                          Default: 60 s.
 * @param {any}      initialData   estado inicial mientras carga la primera vez.
 * @param {Array}    deps          dependencias que fuerzan un re-fetch inmediato.
 */
export default function useFreshData(
    fetcher,
    {
        refreshMs = 5 * 60 * 1000,
        staleAfterMs = 60 * 1000,
        initialData = null,
        deps = [],
    } = {}
) {
    const [data, setData] = useState(initialData);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const lastFetchedRef = useRef(0);
    const inflightRef = useRef(false);

    const run = useCallback(
        async (force = false) => {
            const now = Date.now();
            if (!force && now - lastFetchedRef.current < staleAfterMs) return;
            if (inflightRef.current) return;
            inflightRef.current = true;
            try {
                const result = await fetcher();
                setData(result);
                setError(null);
                lastFetchedRef.current = Date.now();
            } catch (e) {
                setError(e);
            } finally {
                inflightRef.current = false;
                setLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [staleAfterMs, ...deps]
    );

    // Fetch on mount + when deps change
    useEffect(() => {
        run(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    // Refetch on visibility change + focus
    useEffect(() => {
        const onVis = () => {
            if (document.visibilityState === "visible") run(false);
        };
        const onFocus = () => run(false);
        document.addEventListener("visibilitychange", onVis);
        window.addEventListener("focus", onFocus);
        return () => {
            document.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("focus", onFocus);
        };
    }, [run]);

    // Optional polling
    useEffect(() => {
        if (!refreshMs || refreshMs <= 0) return undefined;
        const id = setInterval(() => {
            if (document.visibilityState === "visible") run(false);
        }, refreshMs);
        return () => clearInterval(id);
    }, [refreshMs, run]);

    return { data, loading, error, refresh: () => run(true) };
}
